import { GraphQLNonNull } from 'graphql'
import { Context } from '../shared'
import { ContrastQueryParameters } from '../endpoint-helpers/contrast-query-params'
import { attackEventType, AttackEvent, StackFrame } from '../types/attack-event'
import {
  attackEventQueryArgs,
  AttackEventQueryArgs
} from '../types/attack-event-query-args'
import { ContrastGraphQLFieldConfig } from '..'
import { normalizeAttackEvent } from './attack-events'

interface SummaryInput {
  readonly hidden: boolean
  readonly details: {
    readonly file: string
    readonly method: string
    readonly line: number
    readonly result?: string
    readonly stackFrames: StackFrame[]
    readonly lastCustomFrame: number
    readonly signature: string
    readonly vector: string
  }
  readonly story: {
    readonly chapters: Array<{
      readonly type: string
      readonly introTextFormat?: string
      readonly introTextVariables?: any
      readonly bodyFormat?: string
      readonly bodyFormatVariables?: any
    }>
  }
  readonly request?: {
    readonly chapters: Array<{
      readonly type: 'request'
      readonly introText?: string
      readonly body?: string
    }>
  }
}

// Map essentially "empty" JSON strings to `undefined`
const normalizeMustacheVariables = (vars) => {
  if (typeof vars === 'undefined') {
    return undefined
  }
  const s = JSON.stringify(vars)
  if (s === '{}' || s === '') {
    return undefined
  }
  return s
}

const normalizeChapter = (chapter) =>
  typeof chapter.introTemplate === 'undefined' &&
  typeof chapter.introText === 'undefined' &&
  typeof chapter.bodyTemplate === 'undefined' &&
  typeof chapter.body === 'undefined'
    ? undefined
    : chapter

// Discard chapters with null values other than `type`
const normalizeChapters = (chapters) =>
  chapters
    .map(
      ({
        type,
        introText,
        introTextFormat,
        introTextVariables,
        body,
        bodyFormat,
        bodyFormatVariables
      }) =>
        normalizeChapter({
          type,
          introText,
          introTemplate: introTextFormat ? introTextFormat : undefined,
          introVariables: normalizeMustacheVariables(introTextVariables),
          body,
          bodyTemplate: bodyFormat ? bodyFormat : undefined,
          bodyVariables: normalizeMustacheVariables(bodyFormatVariables)
        })
    )
    .filter((chapter) => typeof chapter !== 'undefined')

const normalizeSummary: (data: SummaryInput) => AttackEventSummary = ({
  hidden,
  details: {
    file,
    method,
    line,
    stackFrames,
    lastCustomFrame,
    signature,
    vector,
    result: summaryDetailsResult
  },
  story: { chapters: storyChapters },
  request
}) => {
  return {
    hidden,
    file,
    method,
    line,
    stackFrames,
    lastCustomFrame,
    signature,
    vector,
    summaryDetailsResult,
    storyChapters: normalizeChapters(storyChapters),
    requestChapters: request ? normalizeChapters(request.chapters) : undefined
  }
}

interface Chapter {
  readonly type: string
  readonly introText?: string
  readonly introTemplate?: string
  readonly introVariables?: string
  readonly body?: string
  readonly bodyTemplate?: string
  readonly bodyVariables?: string
}

interface AttackEventSummary {
  readonly hidden: boolean
  readonly callLocation?: string
  readonly file: string
  readonly method: string
  readonly line: number
  readonly stackFrames: Array<Partial<StackFrame>>
  readonly storyChapters: Chapter[]
  readonly requestChapters: Chapter[]
}

type AttackEventQueryResponse = AttackEventSummary & AttackEvent

export const attackEvent: ContrastGraphQLFieldConfig<
  any,
  Context,
  AttackEventQueryArgs,
  AttackEventQueryResponse
> = {
  description: 'Get an attack event by ID',
  type: new GraphQLNonNull(attackEventType),
  args: attackEventQueryArgs,
  async resolve(_source, args, context) {
    const { attackEventID, organizationID } = args

    const params = new ContrastQueryParameters()
    params.append(
      'expand',
      'event,drilldownDetails,sourceName,application_roles'
    )

    const { summary } = await context.connector.get(
      `/${organizationID}/rasp/events/summary/${attackEventID}?${params}`
    )

    const { tags } = await context.connector.get(
      `/${organizationID}/tags/attack/event/list/${attackEventID}`
    )

    const { event, ...rest } = summary

    return {
      ...normalizeAttackEvent({
        ...event,
        tags
      }),
      ...normalizeSummary(rest)
    }
  }
}
